import * as React from 'react';
import { AdvancedTable, Card, WarningModal } from 'components';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { connect } from 'react-redux';
import { useEffectProcessing, useMultiSearch } from 'hooks';
import { formatFIO, formatPhoneNumber } from '_helpers';
import { IFilterPart, ISearchPart } from 'components/models';
import { PaginationImpl } from 'components/defaultValues';
import { Button } from 'react-bootstrap';
import { UserEditor } from '../../components/UserEditor';
import { ICreateAccUser } from '../../api/ApiClient';

const UsersContainer: React.FC<IProps> = (props) => {
    const { filters, changeFilter } = useMultiSearch<ISearchPart & IFilterPart>(props.fetchUsers, {});
    const [showEditor, setShowEditor] = React.useState(false);
    const [selectedId, selectId] = React.useState<string | null>(null);

    const processing = useEffectProcessing(
      () => props.fetchUsers(filters),
      [],
      () => props.clearUsers(),
    );

    const header = [
        { fieldName: 'fullName', title: 'ИМЯ, ФАМИЛИЯ' },
        { fieldName: 'email', title: 'ПОЧТА' },
        { fieldName: 'phone', title: 'ТЕЛЕФОН' },
    ];

    const rows = props.userList ? props.userList.items.map(u => ({
        id: u.id,
        columns: [
            formatFIO(u),
            u.email,
            formatPhoneNumber(u.phone) || u.phone,
        ],
    })) : [];

    const pagination = new PaginationImpl(props.userList);

    const handleClose = () => {
        setShowEditor(false);
        props.clearUserDetails();
        selectId(null);
    };

    const handleSaveUser = (body: ICreateAccUser) => {
        if (selectedId) {
            return props.updateUser({
                id: selectedId,
                body,
            });
        }

        return props.createUser(body);
    };

    const handleDeleteUser = async () => {
        if (selectedId && await props.deleteUser(selectedId)) {
            await props.fetchUsers(filters);
            props.clearUserDetails();
            setShowEditor(false);
            selectId(null);
        }
    };

    const createButton = React.useMemo(() => (
      <Button
        onClick={() => setShowEditor(true)}
      >
          Добавить администратора
      </Button>
    ), []);

    return (
      <Card
        withoutPadding
        title="Администраторы АЦ"
        category="Список администраторов аккредитационного центра"
        showLoader={processing}
        //headerChild={createButton}
      >
          <AdvancedTable
            data={rows}
            tableHeaders={header}
            handleRowClick={(id: string) => {
                setShowEditor(true);
                selectId(id);
                props.fetchUserDetails(id);
            }}
            handleSearch={(f, t) => changeFilter({
                searchField: f,
                searchTerm: t,
            })}
            handlePageChange={p => changeFilter({ pageSize: p.perPage, page: p.page })}
            pageSize={pagination.pageSize}
            page={pagination.page}
            total={pagination.total}
          />

          <WarningModal
            withoutCancelButton
            footerClassNames="p-0"
            title={selectedId ? (props.userDetails && formatFIO(props.userDetails)) || '' : 'Добавление  администратора АЦ'}
            show={showEditor}
            onHide={handleClose}
            dotsButtons={selectedId ? [{
                buttonText: 'Удалить пользователя',
                iconType: 'delete',
                onClick: handleDeleteUser,
            }] : undefined}
          >
              {showEditor && (
                <UserEditor
                  onSendConfirmation={props.userDetails && !props.userDetails.emailConfirmed
                    ? () => selectedId && props.sendConfirmationEmail(selectedId)
                    : undefined}
                  apiError={props.apiError}
                  details={props.userDetails}
                  accessBtnTittle={selectedId ? 'Сохранить' : 'Добавить администратора'}
                  onCancel={handleClose}
                  processing={props.editLoading}
                  onSubmit={async (f) => {
                      if (await handleSaveUser(f)) {
                          selectId(null);
                          setShowEditor(false);
                          await props.fetchUsers(filters);
                          props.clearUserDetails();
                      }
                  }}
                />
              )}
          </WarningModal>
      </Card>
    );
};

const mapStateToProps = (state: IRootState) => ({
    userList: state.users.list,
    userDetails: state.users.details,
    apiError: state.errors.error,
    editLoading: Boolean(state.loading.effects.users.deleteUserAsync
      || state.loading.effects.users.updateUserAsync
      || state.loading.effects.users.createUserAsync),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchUsers: dispatch.users.fetchUsersAsync,
    clearUsers: dispatch.users.clearList,
    fetchUserDetails: dispatch.users.fetchUserDetailsAsync,
    clearUserDetails: dispatch.users.clearDetails,
    createUser: dispatch.users.createUserAsync,
    updateUser: dispatch.users.updateUserAsync,
    deleteUser: dispatch.users.deleteUserAsync,
    sendConfirmationEmail: dispatch.users.sendConfirmationEmailAsync,
});

type ConnectedProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
type IProps = ConnectedProps;

export default connect(mapStateToProps, mapDispatchToProps)(UsersContainer);
